import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { formatImageUrl } from '../../shared/utils/formatImageUrl.js';
import { dateToString } from '../../shared/utils/parseDate.js';
import { Pagination } from '../../features/Pagination/index.js';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { EmptyState } from '../../shared/ui/EmptyState';
import { CalendarDays, Plus } from 'lucide-react';

const PAGE_SIZE = 5;
const FILTER_KEYS = ['q', 'categoryId', 'formatId'];

const EventsPage = () => {
  const [data, setData] = useState({});
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const categoryId = searchParams.get('categoryId') || null;
  const formatId = searchParams.get('formatId') || null;
  const [searchValue, setSearchValue] = useState(q);
  const [filterValues, setFilterValues] = useState({ categoryId, formatId });
  const [reloadKey, setReloadKey] = useState(0);
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);
  const location = useLocation();

  const hasAppliedFilters = Boolean(q || categoryId || formatId);

  // Черновики поиска и фильтров следуют за URL (назад/вперёд в браузере)
  useEffect(() => {
    setSearchValue(q);
  }, [q]);

  useEffect(() => {
    setFilterValues({ categoryId, formatId });
  }, [categoryId, formatId]);

  useEffect(() => {
    apiRequests.events.getCategories(100, 0).then((res) => setCategories(res));
    apiRequests.events.getFormats(100, 0).then((res) => setFormats(res));
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiRequests.events
      .get(pageSize, offset, q, categoryId, formatId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => console.error(e));
    return () => {
      cancelled = true;
    };
  }, [q, categoryId, formatId, offset, pageSize, reloadKey]);

  const updateParams = (values) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      params.delete('page');
      return params;
    });
  };

  const handleDelete = (id) => {
    apiRequests.events.delete(id).then(() => {
      if (data.items?.length === 1 && page > 1) setPage(page - 1);
      else setReloadKey((k) => k + 1);
    });
  };

  const handleSearch = () => updateParams({ q: searchValue.trim() });

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => updateParams(filterValues);

  const resetFilters = () => {
    setSearchValue('');
    setFilterValues({ categoryId: null, formatId: null });
    updateParams(Object.fromEntries(FILTER_KEYS.map((key) => [key, null])));
  };

  const filters = [
    {
      key: 'categoryId',
      type: 'select',
      label: 'Категория',
      value: filterValues.categoryId,
      placeholder: 'Все категории',
      options: categories?.items?.map((c) => ({ value: c.id, label: c.title })) || [],
    },
    {
      key: 'formatId',
      type: 'select',
      label: 'Формат',
      value: filterValues.formatId,
      placeholder: 'Все форматы',
      options: formats?.items?.map((f) => ({ value: f.id, label: f.title })) || [],
    },
  ];

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск мероприятий..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        createLink="/events/create"
      />

      <div className="mb-3">
        {data.items?.length === 0 ? (
          hasAppliedFilters ? (
            <EmptyState
              icon={CalendarDays}
              variant="search"
              title="Мероприятия не найдены"
              description="Под запрос и выбранные категорию и формат ничего не подходит. Измените формулировку или сбросьте фильтры."
            />
          ) : (
            <EmptyState
              icon={CalendarDays}
              title="Мероприятий пока нет"
              description="Добавьте первое событие — митап, вебинар или конференцию. Даты, обложку и переводы можно заполнить на одной странице."
              action={{ label: 'Добавить мероприятие', to: '/events/create', icon: Plus }}
            />
          )
        ) : (
          data.items?.map((item) => (
            <EntityCard
              key={item.id}
              image={formatImageUrl(item.imgPath)}
              title={item.title}
              fields={[
                { label: 'Формат', value: item.format?.title },
                { label: 'Категория', value: item.category?.title },
                { label: 'Создано', value: dateToString(item.createdAt) },
              ]}
              editLink={`${location.pathname}/${item.id}`}
              onDelete={() => handleDelete(item.id)}
            />
          ))
        )}
      </div>

      <Pagination page={page} total={data.totalCount} pageSize={pageSize} onPageChange={setPage} />
    </>
  );
};

export default EventsPage;
